import { useEffect, useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { listQcHistoryPage, type QcHistorySession, type QcHistoryStatus } from "@/lib/manufacturing/qcDashboardClient";
import { FINAL_QC_QUEUE_START, readFinalQc } from "@/lib/manufacturing/finalQc";
import { readQcCorrection } from "@/lib/manufacturing/qcCorrection";
import { QcReportDeleteButton } from "@/components/manufacturing/qc/QcReportDeleteButton";

const PAGE = 20;
const STATUS_KEY = "mfg-qc-history-status";
const STATUSES = new Set<QcHistoryStatus>(["uncorrected", "open", "passed", "final", "all"]);

function readSavedStatus(): QcHistoryStatus {
  try {
    const saved = window.localStorage.getItem(STATUS_KEY);
    if (saved === "submitted") return "all";
    if (saved && STATUSES.has(saved as QcHistoryStatus)) return saved as QcHistoryStatus;
  } catch {
    /* private browsing can block storage */
  }
  return "uncorrected";
}

function rememberStatus(status: QcHistoryStatus) {
  try {
    window.localStorage.setItem(STATUS_KEY, status);
  } catch {
    /* private browsing can block storage */
  }
}

function labelFor(session: QcHistorySession): string {
  const serial = session.serial_text.trim();
  const model = session.model_hint?.trim();
  if (serial && model) return `${serial} · ${model}`;
  if (serial) return serial;
  if (model) return model;
  return "Inspection";
}

function whenFor(session: QcHistorySession): string {
  const stamp = session.completed_at || session.started_at;
  if (!stamp) return "";
  const date = new Date(stamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function statusLabel(session: QcHistorySession, filter: QcHistoryStatus): string {
  if (filter === "final") return "Waiting on final QC";
  if (session.session_status !== "COMPLETED") return "Not submitted";
  if (session.open_failures > 0) return `${session.open_failures} uncorrected`;
  if (readFinalQc(session.session_summary)) return "Ready for delivery";
  if (readQcCorrection(session.session_summary)) return "Correction submitted";
  if (session.completed_at && session.completed_at < FINAL_QC_QUEUE_START) return "Closed before final QC";
  return "Waiting on final QC";
}

export function BuilderAssistantHistory({
  onOpen,
  onResume,
  onDeleted,
}: {
  onOpen: (sessionId: string) => void;
  onResume: (sessionId: string) => void;
  onDeleted?: (sessionId: string) => void;
}) {
  const [sessions, setSessions] = useState<QcHistorySession[]>([]);
  const [status, setStatus] = useState<QcHistoryStatus>(readSavedStatus);
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listQcHistoryPage({ status, page, sort }).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setError(null);
      setSessions(res.data.sessions);
      setTotal(res.data.total);
    });
    return () => {
      cancelled = true;
    };
  }, [status, page, sort, reload]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <section className="min-w-0 max-w-full space-y-2 overflow-hidden">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">History</h3>
        <div className="flex min-w-0 max-w-full flex-wrap gap-2">
          <select
            className="max-w-full min-w-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-900"
            value={status}
            onChange={(event) => {
              const next = event.target.value as QcHistoryStatus;
              setPage(0);
              setStatus(next);
              rememberStatus(next);
            }}
          >
            <option value="uncorrected">Uncorrected</option>
            <option value="open">Not submitted</option>
            <option value="final">Waiting on final QC</option>
            <option value="passed">Ready for delivery</option>
            <option value="all">All</option>
          </select>
          <select
            className="max-w-full min-w-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-900"
            value={sort}
            onChange={(event) => {
              setPage(0);
              setSort(event.target.value as "newest" | "oldest");
            }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {sessions.length === 0 && !error ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {status === "uncorrected"
            ? "No submitted report has an uncorrected failure."
            : status === "final"
              ? "No building is waiting on final QC."
              : status === "passed"
                ? "No building has passed final QC."
                : "No inspections in this list."}
        </p>
      ) : null}
      <ul className="min-w-0 max-w-full space-y-2">
        {sessions.map((session) => {
          const attention = session.session_status !== "COMPLETED" || session.open_failures > 0;
          return (
            <li key={session.id} className="flex min-w-0 max-w-xl flex-wrap items-start gap-2">
              <button
                type="button"
                className={
                  attention
                    ? `${ghostBtnClass} block min-w-0 max-w-md flex-1 overflow-hidden text-left border-red-500 text-red-800 ring-1 ring-red-500 dark:text-red-200`
                    : `${ghostBtnClass} block min-w-0 max-w-md flex-1 overflow-hidden text-left`
                }
                onClick={() => {
                  if (session.session_status !== "COMPLETED") onOpen(session.id);
                  else if (session.open_failures > 0) onResume(session.id);
                  else onOpen(session.id);
                }}
              >
                <span className="block min-w-0 break-words">{labelFor(session)}</span>
                <span className="mt-1 block min-w-0 break-words text-xs text-gray-500 dark:text-zinc-400">
                  {statusLabel(session, status)}
                  {whenFor(session) ? ` · ${whenFor(session)}` : ""}
                  {session.actor_label ? ` · ${session.actor_label}` : ""}
                </span>
              </button>
              <QcReportDeleteButton
                sessionId={session.id}
                label={labelFor(session)}
                onDeleted={() => {
                  setReload((current) => current + 1);
                  onDeleted?.(session.id);
                }}
              />
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
        <button type="button" className={ghostBtnClass} disabled={page <= 0} onClick={() => setPage((current) => current - 1)}>
          Previous
        </button>
        <span>Page {page + 1} of {pages}</span>
        <button type="button" className={ghostBtnClass} disabled={page + 1 >= pages} onClick={() => setPage((current) => current + 1)}>
          Next
        </button>
      </div>
    </section>
  );
}
